import { useEffect } from 'react'

import { useNavigate, useSearchParams } from 'react-router-dom'
import { IconButton, InputAdornment } from '@mui/material'

import { ButtonComponent } from '../../components/Button'
import { FieldTextComponent } from '../../components/FieldText'
import { LayoutPage } from '../../components/LayoutPage'
import { ListComponent } from '../../components/ListComponent'
import { PageTitleComponent } from '../../components/PageTitleComponent'
import { PaginationComponent } from '../../components/Pagination'
import { SpinnerComponent } from '../../components/Spinner'
import { TabsComponent } from '../../components/Tabs'

import { useAppContext } from '../../hooks/useAppContext'
import { useControlSearch } from '../../hooks/useControlSearch'
import { useSimpleState } from '../../hooks/useSimpleState'
import { useView } from '../../hooks/useView'

import './index.css'

const renderViewDealsMap = {
    0: 'new',
    1: 'published',
    2: 'reportedDead',
    3: 'dead',
    4: 'unpublished',
    5: 'deleted'
}

const renderViewDealsParamsMap = {
    0: 'publishStartDate[eq]=null&publishEndDate[eq]=null&deleteDate[eq]=null',
    1: 'publishStartDate[ne]=null&publishEndDate[eq]=null&deleteDate[eq]=null',
    2: 'clicksDead[gt]=0&deadDate[eq]=null&deleteDate[eq]=null',
    3: 'deadDate[ne]=null&deleteDate[eq]=null',
    4: 'publishEndDate[ne]=null&publishStartDate[eq]=null&deleteDate[eq]=null',
    5: 'deleteDate[ne]=null',
}

const SearchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
)

const ClearIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
)

const getInitialTab = (searchParams) => {
    const fromUrl = parseInt(searchParams.get('tab'), 10)
    return Number.isInteger(fromUrl) && renderViewDealsParamsMap[fromUrl] !== undefined ? fromUrl : 0
}

const getInitialPage = (searchParams) => {
    const fromUrl = parseInt(searchParams.get('page'), 10)
    return Number.isInteger(fromUrl) && fromUrl > 0 ? fromUrl : 1
}

const ManageDealsPage = () => {
    const {ac} = useAppContext()

    const navigate = useNavigate()

    const [searchParams, setSearchParams] = useSearchParams()

    const { _state } = useSimpleState({
        tab: getInitialTab(searchParams)
    })

    const { _state: _searchInput } = useSimpleState('')

    const {_view: _viewMain} = useView({
        initialView: 'main',
        views: {
            loading: renderLoading,
            main: renderMain
        }
    })

    const {_view: _viewDeals} = useView({
        initialView: 'loading',
        views: {
            loading: renderDealsLoading,
            new: renderDealsNew,
            published: renderDealsPublished,
            reportedDead: renderDealsReportedDead,
            unpublished: renderDealsUnpublished,
            dead: renderDealsDead,
            deleted: renderDealsDeleted
        }
    })

    const {_control, _results, _params, _page, _search, _sort} = useControlSearch({
        params: renderViewDealsParamsMap[_state.get('tab')],
        page: getInitialPage(searchParams),
        beforeFetch: () => {
            _viewDeals.set('loading')
        },
        afterFetch: () => {
            _viewDeals.set(renderViewDealsMap[_state.get('tab')])
        }
    })

    // debounce typed search input before it drives an actual fetch —
    // React 18 batches both state updates below into one re-render,
    // so this never fires two requests for one keystroke pause
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (_search.get() !== _searchInput.get()) {
                _search.set(_searchInput.get())
                _page.set(1)
            }
        }, 300)

        return () => clearTimeout(timeout)
    }, [_searchInput.get()])

    const handleTabChange = (val) => {
        _state.set('tab', val)
        _params.set(renderViewDealsParamsMap[val])
        _page.set(1)
        setSearchParams({ tab: String(val), page: '1' }, { replace: true })
    }

    const handlePageChange = (page) => {
        _control.goToPage(page)
        setSearchParams({ tab: String(_state.get('tab')), page: String(page) }, { replace: true })
    }

    const handleSort = (column) => {
        _control.setSort(column)
    }

    const handleGoToEdit = (val) => {
        navigate(`/edit-deal/${val.id}`)
    }

    const listSortConfig = { sortBy: _sort.get('sortBy'), sortDir: _sort.get('sortDir') }
    const listSortEvents = { handleSort, handleEdit: handleGoToEdit }

    function renderLoading() {
        return (
            <div>
                <SpinnerComponent />
            </div>
        )
    }

    function renderMain() {
        return (
            <div>
                <LayoutPage config={{ id: 'ManageDeals' }}>
                    <div className="manageDeals-header">
                        <PageTitleComponent value="Manage Deals" />
                        <ButtonComponent events={{ handleClick: () => navigate('/create-deals') }}>
                            + New Deal
                        </ButtonComponent>
                    </div>

                    <TabsComponent value={_state.get('tab')} options={[
                        {
                            title: 'New',
                        },
                        {
                            title: 'Published',
                        },
                        {
                            title: 'Reported Dead',
                        },
                        {
                            title: 'Dead',
                        },
                        {
                            title: 'Unpublished',
                        },
                        {
                            title: 'Deleted'
                        }
                    ]} config={{
                        type: 'simple'
                    }} events={{
                        handleChange: handleTabChange
                    }} />

                    {/* header and footer control bars bracket the list, same
                        as a table's own structure — pagination repeated at
                        both ends so it's reachable without scrolling back
                        up once you're deep in a long list */}
                    <div className="manageDeals-panel">
                        <div className="manageDeals-controlBar">
                            <FieldTextComponent
                                value={_searchInput.get()}
                                config={{
                                    placeholder: 'Search deals',
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: _searchInput.get() ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => _searchInput.set('')} aria-label="Clear search">
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : undefined
                                }}
                                events={{ handleChange: (val) => _searchInput.set(val) }}
                            />

                            {/* collapses to nothing when there's only one page —
                                the list header's sticky offset (see ManageDeals.css)
                                adapts to that via a :has() selector rather than this
                                needing to keep reserving space it doesn't use */}
                            <div className="manageDeals-controlBarPaginationSlot">
                                {_results.get()?.body?.meta?.lastPage > 1 ? (
                                    <PaginationComponent
                                        value={_page.get()}
                                        config={{ lastPage: _results.get()?.body?.meta?.lastPage, size: 'small' }}
                                        events={{ handleChange: handlePageChange }}
                                    />
                                ) : null}
                            </div>
                        </div>

                        <div className="manageDeals-dealsContainer" key={JSON.stringify(_results.get())}>
                            {_viewDeals.get()}
                        </div>

                        {_results.get()?.body?.meta?.lastPage > 1 ? (
                            <div className="manageDeals-footerBar">
                                <PaginationComponent
                                    value={_page.get()}
                                    config={{ lastPage: _results.get()?.body?.meta?.lastPage, size: 'small' }}
                                    events={{ handleChange: handlePageChange }}
                                />
                            </div>
                        ) : null}
                    </div>
                </LayoutPage>
            </div>
        )
    }

    function renderDealsLoading() {
        return (
            <div className="manageDeals-deals">
                <SpinnerComponent />
            </div>
        )
    }

    function renderDealsNew() {
        let results = _results.get()

        return (
            <div className="manageDeals-deals">
                {results?.body?.data?.length ? (
                    <ListComponent value={results.body.data} key={JSON.stringify(results)} config={{
                        ...listSortConfig,
                        controls: (item) => (
                            <>
                                <ButtonComponent config={{
                                    palette: 'blue'
                                }} events={{
                                    handleClick: () => _control.fetchPublish(item, true)
                                }}>
                                    Publish
                                </ButtonComponent>

                                <ButtonComponent config={{
                                    palette: 'blue'
                                }} events={{
                                    handleClick: () => handleGoToEdit(item)
                                }}>
                                    Edit
                                </ButtonComponent>

                                <ButtonComponent config={{
                                    palette: 'gray'
                                }} events={{
                                    handleClick: () => _control.fetchDelete(item, true)
                                }}>
                                    Delete
                                </ButtonComponent>
                            </>
                        )
                    }} events={listSortEvents} />
                ) : (
                    <div className="manageDeals-empty">No new deals right now.</div>
                )}
            </div>
        )
    }

    function renderDealsPublished() {
        let results = _results.get()

        return (
            <div className="manageDeals-deals">
                {results?.body?.data?.length ? (
                    <ListComponent value={results.body.data} key={JSON.stringify(results)} config={{
                        ...listSortConfig,
                        controls: (item) => (
                            <>
                                <ButtonComponent config={{
                                    palette: 'blue'
                                }} events={{
                                    handleClick: () => _control.fetchPublish(item, false)
                                }}>
                                    Unpublish
                                </ButtonComponent>

                                <ButtonComponent config={{
                                    palette: 'red'
                                }} events={{
                                    handleClick: () => _control.fetchDead(item, true)
                                }}>
                                    Mark Dead
                                </ButtonComponent>

                                <ButtonComponent config={{
                                    palette: 'blue'
                                }} events={{
                                    handleClick: () => handleGoToEdit(item)
                                }}>
                                    Edit
                                </ButtonComponent>

                                <ButtonComponent config={{
                                    palette: 'gray'
                                }} events={{
                                    handleClick: () => _control.fetchDelete(item, true)
                                }}>
                                    Delete
                                </ButtonComponent>
                            </>
                        )
                    }} events={listSortEvents} />
                ) : (
                    <div className="manageDeals-empty">No published deals.</div>
                )}
            </div>
        )
    }

    function renderDealsReportedDead() {
        let results = _results.get()

        return (
            <div className="manageDeals-deals">
                {results?.body?.data?.length ? (
                    <ListComponent value={results.body.data} key={JSON.stringify(results)} config={{
                        ...listSortConfig,
                        controls: (item) => (
                            <>
                                <ButtonComponent config={{
                                    palette: 'amber'
                                }} events={{
                                    handleClick: () => _control.fetchResetClicksDead(item)
                                }}>
                                    Unmark Reported
                                </ButtonComponent>

                                <ButtonComponent config={{
                                    palette: 'red'
                                }} events={{
                                    handleClick: () => _control.fetchDead(item, true)
                                }}>
                                    Mark Dead
                                </ButtonComponent>

                                <ButtonComponent config={{
                                    palette: 'blue'
                                }} events={{
                                    handleClick: () => handleGoToEdit(item)
                                }}>
                                    Edit
                                </ButtonComponent>

                                <ButtonComponent config={{
                                    palette: 'gray'
                                }} events={{
                                    handleClick: () => _control.fetchDelete(item, true)
                                }}>
                                    Delete
                                </ButtonComponent>
                            </>
                        )
                    }} events={listSortEvents} />
                ) : (
                    <div className="manageDeals-empty">Nothing reported dead.</div>
                )}
            </div>
        )
    }

    function renderDealsUnpublished() {
        let results = _results.get()

        return (
            <div className="manageDeals-deals">
                {results?.body?.data?.length ? (
                    <ListComponent value={results.body.data} key={JSON.stringify(results)} config={{
                        ...listSortConfig,
                        controls: (item) => (
                            <>
                                <ButtonComponent config={{
                                    palette: 'blue'
                                }} events={{
                                    handleClick: () => _control.fetchPublish(item, true)
                                }}>
                                    Publish
                                </ButtonComponent>

                                <ButtonComponent config={{
                                    palette: 'blue'
                                }} events={{
                                    handleClick: () => handleGoToEdit(item)
                                }}>
                                    Edit
                                </ButtonComponent>

                                <ButtonComponent config={{
                                    palette: 'gray'
                                }} events={{
                                    handleClick: () => _control.fetchDelete(item, true)
                                }}>
                                    Delete
                                </ButtonComponent>
                            </>
                        )
                    }} events={listSortEvents} />
                ) : (
                    <div className="manageDeals-empty">No unpublished deals.</div>
                )}
            </div>
        )
    }

    function renderDealsDead() {
        let results = _results.get()

        return (
            <div className="manageDeals-deals">
                {results?.body?.data?.length ? (
                    <ListComponent value={results.body.data} key={JSON.stringify(results)} config={{
                        ...listSortConfig,
                        controls: (item) => (
                            <>
                                <ButtonComponent config={{
                                    palette: 'red'
                                }} events={{
                                    handleClick: () => _control.fetchDead(item, false)
                                }}>
                                    Unmark Dead
                                </ButtonComponent>

                                <ButtonComponent config={{
                                    palette: 'blue'
                                }} events={{
                                    handleClick: () => handleGoToEdit(item)
                                }}>
                                    Edit
                                </ButtonComponent>

                                <ButtonComponent config={{
                                    palette: 'gray'
                                }} events={{
                                    handleClick: () => _control.fetchDelete(item, true)
                                }}>
                                    Delete
                                </ButtonComponent>
                            </>
                        )
                    }} events={listSortEvents} />
                ) : (
                    <div className="manageDeals-empty">No dead deals.</div>
                )}
            </div>
        )
    }

    function renderDealsDeleted() {
        let results = _results.get()

        return (
            <div className="manageDeals-deals">
                {results?.body?.data?.length ? (
                    <ListComponent value={results.body.data} key={JSON.stringify(results)} config={{
                        ...listSortConfig,
                        controls: (item) => (
                            <>
                                <ButtonComponent config={{
                                    palette: 'blue'
                                }} events={{
                                    handleClick: () => handleGoToEdit(item)
                                }}>
                                    Edit
                                </ButtonComponent>

                                <ButtonComponent config={{
                                    palette: 'gray'
                                }} events={{
                                    handleClick: () => _control.fetchDelete(item, false)
                                }}>
                                    Undelete
                                </ButtonComponent>
                            </>
                        )
                    }} events={listSortEvents} />
                ) : (
                    <div className="manageDeals-empty">No deleted deals.</div>
                )}
            </div>
        )
    }

    return _viewMain.get()
}

export { ManageDealsPage }